import { useState, useRef, useCallback, useEffect } from 'react';

const PULL_THRESHOLD = 80;
const RESISTANCE_FACTOR = 2.5;

export default function usePullToRefresh(onRefresh) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchRef = useRef({ startY: 0, active: false });

  const handleTouchStart = useCallback((e) => {
    if (isRefreshing) return;
    if (window.scrollY !== 0) return;

    touchRef.current = {
      startY: e.touches[0].clientY,
      active: true,
    };
  }, [isRefreshing]);

  const handleTouchMove = useCallback((e) => {
    const touch = touchRef.current;
    if (!touch.active) return;
    if (window.scrollY !== 0) {
      touch.active = false;
      setPullDistance(0);
      return;
    }

    const dy = e.touches[0].clientY - touch.startY;
    if (dy < 0) {
      setPullDistance(0);
      return;
    }

    e.preventDefault();
    const distance = dy / RESISTANCE_FACTOR;
    setPullDistance(distance);
  }, []);

  const handleTouchEnd = useCallback(() => {
    const touch = touchRef.current;
    if (!touch.active) return;
    touch.active = false;

    if (pullDistance >= PULL_THRESHOLD) {
      setIsRefreshing(true);
      setPullDistance(PULL_THRESHOLD);

      // Trigger refresh
      const result = onRefresh();
      if (result && typeof result.then === 'function') {
        result.then(() => {
          setIsRefreshing(false);
          setPullDistance(0);
        });
      } else {
        // If not a promise, just animate back after a short delay
        setTimeout(() => {
          setIsRefreshing(false);
          setPullDistance(0);
        }, 800);
      }
    } else {
      setPullDistance(0);
    }
  }, [pullDistance, onRefresh]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    if (!mq.matches) return;

    const passiveOpts = { passive: true };
    const activeOpts = { passive: false };

    document.addEventListener('touchstart', handleTouchStart, passiveOpts);
    document.addEventListener('touchmove', handleTouchMove, activeOpts);
    document.addEventListener('touchend', handleTouchEnd, passiveOpts);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return { pullDistance, isRefreshing };
}
